import { MutableRefObject, useCallback, useMemo, useRef, useState } from 'react';

export const useToggle = (init: boolean) => {
  const [isOn, setIsOn] = useState(init);
  const toggle = useCallback(() => setIsOn((isOn) => !isOn), []);
  const toggleOn = useCallback(() => setIsOn(true), []);
  const toggleOff = useCallback(() => setIsOn(false), []);
  return [isOn, toggle, toggleOn, toggleOff] as const;
};

export const usePopupToggle = (init: boolean) => {
  const [isOn, toggle, _toggleOn, _toggleOff] = useToggle(init);
  const focusRef = useRef<HTMLElement | null>(null);
  const toggleOn = useCallback(() => {
    focusRef.current = document.activeElement as HTMLElement | null;
    _toggleOn();
  }, [_toggleOn]);
  const toggleOff = useCallback(() => {
    _toggleOff();
    focusRef.current?.focus();
  }, [_toggleOff]);
  return [isOn, toggle, toggleOn, toggleOff] as const;
};

/**
 * Use this for a bunch of toggles where only one should be open at the same time
 * e.g. biobubbles, a list of related dropdowns, etc.
 *
 * To use, this would just replace `useToggle` for independently open toggles.
 *
 * @param singletonRef the ref object to keep track of which one is supposed to be open
 * @returns the same tuple as `useToggle`
 */
export type SingleToggleRefValue = null | (() => void);
export type SingleToggleRef = MutableRefObject<SingleToggleRefValue>;
export const useSingleToggle = (singletonRef: SingleToggleRef) => {
  const [_showing, , _show, _hide] = useToggle(false);

  const hide = useCallback(() => {
    _hide();
    if (singletonRef.current === _hide) {
      singletonRef.current = null;
    }
  }, [_hide, singletonRef]);

  /*
    When toggle A opens, it also registered its closing function on the ref
    So that when toggle B opens, it will also check to see any closing function
    is present and use that to close A.
    It does an additional check to see that if its trying to open something 
    that is already open and does nothing to avoid causing visual changes
  */
  const show = useCallback(() => {
    if (singletonRef.current !== _hide) {
      if (singletonRef.current) {
        singletonRef.current();
      }
      singletonRef.current = _hide;
      _show();
    }
    singletonRef.current = _hide;
    _show();
  }, [_hide, _show, singletonRef]);

  const toggle = useCallback(
    (set?: boolean) => {
      if (typeof set === 'boolean') {
        if (set) {
          show();
        } else {
          hide();
        }
      } else {
        if (_showing) {
          hide();
        } else {
          show();
        }
      }
    },
    [_showing, hide, show]
  );

  const showing = _showing && singletonRef.current === _hide;

  return useMemo(() => [showing, toggle, show, hide] as const, [hide, show, showing, toggle]);
};

export const useMultiToggle = (initial: boolean[], onlyOneOpen = false) => {
  const [isOnList, setIsOn] = useState<boolean[]>(initial);

  const toggler = useCallback(
    (mapper: (on: boolean) => boolean) => (target: number) =>
      setIsOn((isOnList) =>
        isOnList.map((on, index) => {
          if (index === target) {
            return mapper(on);
          } else if (onlyOneOpen) {
            return false;
          } else {
            return on;
          }
        })
      ),
    [onlyOneOpen]
  );

  const toggle = useCallback((target: number) => toggler((a) => !a)(target), [toggler]);
  const toggleOn = useCallback((target: number) => toggler(() => true)(target), [toggler]);
  const toggleOff = useCallback((target: number) => toggler(() => false)(target), [toggler]);

  return [isOnList, toggle, toggleOn, toggleOff, setIsOn] as const;
};

export const useToggleWithEffect = (initial: boolean, onToggle: (state: boolean) => void) => {
  const [showIcon, , _show, _hide] = useToggle(initial);

  return useMemo(() => {
    const show = () => {
      _show();
      onToggle(true);
    };

    const hide = () => {
      _hide();
      onToggle(false);
    };

    const toggle = () => {
      showIcon ? hide() : show();
    };
    return [showIcon, toggle, show, hide] as const;
  }, [showIcon, _show, onToggle, _hide]);
};
